/**
 * 409 応答で返す理由コード。
 * 同一ステータスで複数の原因を区別するために用いる。
 *
 * @warning mypage-api の spec (spec/routes/accounts/main.tsp) が同じ値を literal で宣言している。
 * 追加・変更時は spec 側も更新すること。
 */
export const ErrorCode = {
  /** 統合先アカウントのメールアドレスが教務システムの保護者メールアドレスと重複している */
  GuarantorEmailConflict: 'guarantor_email_conflict',
  /** 既にサブメールアドレスが登録済み (アカウント統合済み) のアカウントに対する統合操作 */
  AccountAlreadyIntegrated: 'account_already_integrated',
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

const errorCodes: readonly string[] = Object.values(ErrorCode);

function isErrorCode(value: unknown): value is ErrorCodeValue {
  return typeof value === 'string' && errorCodes.includes(value);
}

/**
 * onError が返すエラーボディから理由コードを取り出す。
 * onError は HTTPError.data を JSON 文字列として data フィールドに載せるため、ここで復元する。
 *
 * 理由コードを付与していない旧バージョンからの応答も受け取りうるため、
 * 取り出せない場合は undefined を返し、呼び出し側で既定の扱いにフォールバックさせる。
 */
export function extractErrorCode(body: unknown): ErrorCodeValue | undefined {
  if (typeof body !== 'object' || body === null || !('data' in body)) {
    return undefined;
  }

  const { data } = body as { data: unknown };
  if (typeof data !== 'string') {
    return undefined;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return undefined;
  }

  if (typeof parsed !== 'object' || parsed === null || !('code' in parsed)) {
    return undefined;
  }

  const { code } = parsed as { code: unknown };
  return isErrorCode(code) ? code : undefined;
}
